import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateProduct, useUpdateProduct } from '@/hooks/useProducts';
import type { Product } from '@/types/product';

interface ProductFormDialogProps {
  open: boolean;
  onClose: () => void;
  product?: Product;
}

export function ProductFormDialog({ open, onClose, product }: ProductFormDialogProps) {
  const isEdit = !!product;
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();

  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [sku, setSku] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      if (product) {
        setName(product.name);
        setPrice(product.price != null ? String(product.price) : '');
        setCurrency(product.currency || 'EUR');
        setSku(product.sku || '');
        setDescription(product.description || '');
        setIsActive((product.metadata as Record<string, unknown>)?.is_active !== false);
      } else {
        setName('');
        setPrice('');
        setCurrency('EUR');
        setSku('');
        setDescription('');
        setIsActive(true);
      }
    }
  }, [open, product]);

  if (!open) return null;

  const isPending = createMutation.isPending || updateMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name ist ein Pflichtfeld.');
      return;
    }

    const parsedPrice = price ? parseFloat(price) : undefined;
    if (parsedPrice != null && (isNaN(parsedPrice) || parsedPrice < 0)) {
      setError('Preis muss eine positive Zahl sein.');
      return;
    }

    const input = {
      name: trimmedName,
      ...(parsedPrice != null && { price: parsedPrice }),
      ...(currency && { currency }),
      ...(sku.trim() && { sku: sku.trim() }),
      ...(description.trim() && { description: description.trim() }),
      is_active: isActive,
    };

    try {
      if (isEdit && product) {
        await updateMutation.mutateAsync({ productId: product.productId, data: input });
      } else {
        await createMutation.mutateAsync(input);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        role="button"
        tabIndex={-1}
        aria-label="Dialog schließen"
      />
      <div className="relative z-50 w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg mx-4">
        <h2 className="text-lg font-semibold">
          {isEdit ? 'Produkt bearbeiten' : 'Neues Produkt'}
        </h2>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="product-name">Name *</Label>
            <Input
              id="product-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Produktname"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="product-price">Preis</Label>
              <Input
                id="product-price"
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="product-currency">W&auml;hrung</Label>
              <select
                id="product-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="EUR">EUR</option>
                <option value="CHF">CHF</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-sku">Artikelnummer (SKU)</Label>
            <Input
              id="product-sku"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="Wird automatisch generiert"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-description">Beschreibung</Label>
            <textarea
              id="product-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Produktbeschreibung"
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="product-active"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="product-active">Aktiv</Label>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Wird gespeichert...' : isEdit ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
